/**
 * THE COUNTRY DASHBOARD'S PERIOD SECTIONS — v19 D3, the Country Manager's view, mounted by
 * `features/dashboard/DashboardPage.tsx` under the stage-count cards.
 *
 * ===========================================================================
 * SCOPED BY THE GUARD, NEVER BY A PICKER
 * ===========================================================================
 * `GET /countries/:cc/dashboard` — the country is the one in the URL, proven by
 * `CountryScopeGuard`; there is no country control on this screen and no `?country=` the
 * reader could widen. Sections arrive PER SEAT: `pipeline`/`renewals` under `quotes.view`,
 * `work`/`signoffs` under `crf.view`, `inquiries` under `inquiries.view` — ABSENT when the
 * seat lacks the key, never zeroed. This component renders what arrived; an absent section
 * is absent here too. A seat holding none of the three keys gets nothing from this
 * component at all (not a frame with blanks), so the dashboard above it stays as it was.
 *
 * ===========================================================================
 * WHAT THE PERIOD APPLIES TO — AND WHAT IT DOES NOT
 * ===========================================================================
 * The board is the board: pipeline by stage, work by column, in flight, sign-offs and
 * renewals-due describe the CURRENT state. The window applies to the FLOWS — quotes sent /
 * confirmed (conversion), inquiries received — each with its delta against the previous
 * equal-length window, computed by the SERVER in the country's calendar. Nothing here
 * subtracts dates.
 *
 * MONEY: `grand_total` (pipeline value by stage) and `amount` (work in flight) arrive per
 * CURRENCY, masked per country — `'•••'` for a seat without `amounts.view` — and render
 * through `ServerAmount`. Nothing is summed across currencies on the client (the one
 * cross-currency figure in the product is the group view's, behind the FX gate).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useSession } from '../../auth/session';
import { Alert, SkeletonBlock } from '../../components/ui';
import type { CountryCode } from '../../domain/countries';
import { ServerAmount } from '../quotes/money';
import { formatDateOnly } from '../renewals/format';
import { dashboardErrorText, dashboardKeys, fetchCountryDashboard } from './api';
import type { CountryDashboardDto, PipelineStageDto, WorkSectionDto } from './api';
import { CountBars, DistributionBar, KpiTile } from './dataviz';
import { WORK_COLUMN_TONE, quoteStageFacts, workColumnLabel } from './labels';
import { PeriodFilter } from './PeriodFilter';
import { readPeriodQuery } from './periods';

export function CountryDashboardSections({ countryCode }: { countryCode: CountryCode }): React.JSX.Element | null {
  const { can } = useSession();
  // The three keys the SERVER composes by — the same three, so the screen never asks for a
  // section the seat cannot hold, and never hides one it can.
  const holdsAny =
    can(countryCode, 'inquiries.view') || can(countryCode, 'quotes.view') || can(countryCode, 'crf.view');

  const [params] = useSearchParams();
  const period = useMemo(() => readPeriodQuery(params), [params]);

  const query = useQuery({
    queryKey: dashboardKeys.country(countryCode, period),
    queryFn: ({ signal }) => fetchCountryDashboard(countryCode, period, signal),
    enabled: holdsAny,
  });

  if (!holdsAny) return null;

  return (
    <section aria-labelledby="country-dashboard-title" className="flex flex-col gap-4" data-testid="country-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="country-dashboard-title" className="text-h2 text-fg">
          Pipeline and work
        </h2>
        <PeriodFilter id="dash" resolved={query.data?.window} />
      </div>

      {query.isPending ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-busy="true" aria-label="Loading the dashboard">
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
          <SkeletonBlock className="h-[92px]" />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title="The dashboard could not be loaded">
          <p>{dashboardErrorText(query.error)}</p>
          <button
            type="button"
            onClick={() => {
              void query.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : (
        <DashboardBody data={query.data} />
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── body ── */

function DashboardBody({ data }: { data: CountryDashboardDto }): React.JSX.Element {
  const { pipeline, work, signoffs, renewals, inquiries } = data;
  const conversion = pipeline?.conversion;
  const deltaLabel = 'vs previous window';

  return (
    <div className="flex flex-col gap-4">
      {/* ── KPI tiles: one per fact the seat may read; an absent section draws no tile ── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="kpi-row">
        {conversion === undefined ? null : (
          <>
            <KpiTile
              testId="kpi-quotes-sent"
              label="Quotes sent"
              value={conversion.quotes_sent}
              delta={conversion.quotes_sent - conversion.previous.quotes_sent}
              deltaLabel={deltaLabel}
            />
            <KpiTile
              testId="kpi-orders-confirmed"
              label="Orders confirmed"
              value={conversion.orders_confirmed}
              delta={conversion.orders_confirmed - conversion.previous.orders_confirmed}
              deltaLabel={deltaLabel}
            />
            <KpiTile
              testId="kpi-conversion"
              label="Quote-to-order"
              value={conversion.rate_pct === null ? '—' : `${String(conversion.rate_pct)}%`}
              delta={conversion.delta_pct}
              deltaLabel={`pts ${deltaLabel}`}
              detail={conversion.rate_pct === null ? 'Nothing was sent in this window.' : undefined}
            />
          </>
        )}
        {inquiries === undefined ? null : (
          <KpiTile
            testId="kpi-inquiries"
            label="Inquiries received"
            value={inquiries.created.count}
            delta={inquiries.created.delta}
            deltaLabel={deltaLabel}
          />
        )}
        {renewals === undefined ? null : (
          <KpiTile
            testId="kpi-renewals"
            label="Renewals due"
            value={renewals.due}
            detail={`Through ${formatDateOnly(renewals.window.to)}`}
          />
        )}
        {signoffs === undefined ? null : (
          <KpiTile
            testId="kpi-signoffs"
            label="Sign-offs overdue"
            value={signoffs.overdue}
            detail={`of ${String(signoffs.waiting)} waiting · ${String(signoffs.overdue_after_days)}+ days`}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {pipeline === undefined ? null : <PipelineCard stages={pipeline.by_stage} currency={data.currency} />}
        {work === undefined ? null : <WorkCard work={work} />}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────── pipeline card ── */

function PipelineCard({ stages, currency }: { stages: readonly PipelineStageDto[]; currency: string }): React.JSX.Element {
  const segments = stages.map((entry) => {
    const facts = quoteStageFacts(entry.stage);
    return { key: entry.stage, label: facts.label, count: entry.count, toneClass: facts.toneClass };
  });
  const total = stages.reduce((sum, entry) => sum + entry.count, 0);
  /*
   * THE CURRENCIES ACTUALLY PRESENT (2026-09-22). The Value column is already per currency
   * (`by_currency`), but this caption named the country's own currency alone — which, once
   * Congo quotes in CDF AND USD, would say "values in CDF" over USD figures. The country's
   * currency leads; any other present follows. An empty pipeline keeps the old caption.
   */
  const present = new Set(stages.flatMap((entry) => entry.by_currency.map((cell) => cell.currency)));
  const shown = [currency, ...[...present].filter((code) => code !== currency).sort()].filter(
    (code) => code === currency || present.has(code),
  );
  return (
    <section aria-labelledby="pipeline-title" className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5" data-testid="pipeline-card">
      <header className="flex items-baseline justify-between gap-3">
        <h3 id="pipeline-title" className="text-h3 text-fg">Pipeline by stage</h3>
        <span className="tnum text-body13 text-fg-secondary">{total} quotes · values in {shown.join(', ')}</span>
      </header>
      <DistributionBar segments={segments} caption="Quotes by stage, as a share of all quotes in this country" testId="pipeline-bar" />
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">Quote count and value by stage</caption>
          <thead>
            <tr className="h-9 border-b border-line bg-subtle">
              <th scope="col" className="px-3 text-th uppercase text-fg">Stage</th>
              <th scope="col" className="px-3 text-th uppercase text-fg">Quotes</th>
              <th scope="col" className="px-3 text-th uppercase text-fg">Value</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((entry) => (
              <tr key={entry.stage} data-stage={entry.stage} className="crf-register-row border-b border-line last:border-b-0">
                <td className="px-3 align-middle text-body13 text-fg">{quoteStageFacts(entry.stage).label}</td>
                <td className="tnum px-3 align-middle text-body13 text-fg">{entry.count}</td>
                <td className="px-3 align-middle text-body13 text-fg">
                  {entry.by_currency.length === 0 ? (
                    <span className="text-fg-secondary">—</span>
                  ) : (
                    <div className="flex flex-col">
                      {entry.by_currency.map((cell) => (
                        <ServerAmount key={cell.currency} value={cell.grand_total} currency={cell.currency} />
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── work card ── */

function WorkCard({ work }: { work: WorkSectionDto }): React.JSX.Element {
  const rows = work.by_column.map((entry) => ({
    key: entry.column,
    label: workColumnLabel(entry.column),
    count: entry.count,
    toneClass: WORK_COLUMN_TONE[entry.column],
  }));
  return (
    <section aria-labelledby="work-title" className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5" data-testid="work-card">
      <header className="flex items-baseline justify-between gap-3">
        <h3 id="work-title" className="text-h3 text-fg">Work in flight</h3>
        <span className="tnum text-body13 text-fg-secondary">
          {work.in_flight.count} {work.in_flight.count === 1 ? 'document' : 'documents'} · {work.stuck} stuck {work.stuck_after_days}+ days
        </span>
      </header>
      <CountBars rows={rows} caption="Open work orders per board column" testId="work-bars" />
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body13">
        <dt className="text-fg-secondary">Value in flight</dt>
        <dd className="flex flex-col text-fg" data-testid="in-flight-value">
          {work.in_flight.by_currency.length === 0 ? (
            <span className="text-fg-secondary">—</span>
          ) : (
            work.in_flight.by_currency.map((cell) => (
              <span key={cell.currency}>
                <ServerAmount value={cell.amount} currency={cell.currency} />
                <span className="text-fg-secondary"> · {cell.count} {cell.count === 1 ? 'document' : 'documents'}</span>
              </span>
            ))
          )}
        </dd>
      </dl>
    </section>
  );
}
